import { useState, useEffect } from 'react';

interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function BookingModal({ isOpen, onClose }: BookingModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    serviceType: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.message.length > 500) return;
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => body.append(key, value));

      const response = await fetch('https://readdy.ai/api/form/d6i6godpk1ctp7d9bca0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', date: '', time: '', serviceType: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const timeSlots = [
    '9:00 AM', '10:00 AM', '11:00 AM',
    '12:00 PM', '1:00 PM', '2:00 PM',
    '3:00 PM', '4:00 PM', '5:00 PM',
  ];

  const today = new Date().toISOString().split('T')[0];

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white z-10 px-8 pt-8 pb-4 border-b border-gray-100">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center space-x-2 mb-1">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-calendar-check-line text-teal-600 text-lg"></i>
                </div>
                <span className="text-xs font-semibold text-teal-600 uppercase tracking-widest">40% Off — Limited Time</span>
              </div>
              <h2 className="text-2xl font-serif font-bold text-gray-900">Book a Consultation</h2>
              <p className="text-sm text-gray-500 mt-1">
                Schedule a 30-minute session ·{' '}
                <span className="line-through text-gray-400">₦1,000,000</span>{' '}
                <span className="text-teal-600 font-semibold">₦600,000</span>
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-gray-600 text-lg"></i>
            </button>
          </div>
        </div>

        {/* Form */}
        <div className="px-8 py-6">
          {submitStatus === 'success' ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-check-line text-teal-600 text-3xl"></i>
              </div>
              <h3 className="text-xl font-serif font-bold text-gray-900 mb-2">Consultation Booked!</h3>
              <p className="text-gray-600 mb-6">
                Thank you! We've received your request and will confirm your appointment within 24 hours.
              </p>
              <button
                onClick={onClose}
                className="px-8 py-3 bg-teal-600 text-white rounded-full font-medium hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} data-readdy-form id="booking-consultation-form" className="space-y-5">
              {/* Name & Email */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="booking-name" className="block text-sm font-medium text-gray-800 mb-1.5">
                    Full Name <span className="text-teal-600">*</span>
                  </label>
                  <input
                    type="text"
                    id="booking-name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Jane Doe"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm outline-none"
                  />
                </div>
                <div>
                  <label htmlFor="booking-email" className="block text-sm font-medium text-gray-800 mb-1.5">
                    Email Address <span className="text-teal-600">*</span>
                  </label>
                  <input
                    type="email"
                    id="booking-email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="jane@example.com"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm outline-none"
                  />
                </div>
              </div>

              {/* Phone & Service */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="booking-phone" className="block text-sm font-medium text-gray-800 mb-1.5">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="booking-phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="07062910646"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm outline-none"
                  />
                </div>
                <div>
                  <label htmlFor="booking-service" className="block text-sm font-medium text-gray-800 mb-1.5">
                    Service Type <span className="text-teal-600">*</span>
                  </label>
                  <select
                    id="booking-service"
                    name="serviceType"
                    required
                    value={formData.serviceType}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm outline-none cursor-pointer"
                  >
                    <option value="">Select a service</option>
                    <option value="Residential Design">Residential Design</option>
                    <option value="Commercial Design">Commercial Design</option>
                    <option value="Renovation Consultation">Renovation Consultation</option>
                    <option value="Space Planning">Space Planning</option>
                    <option value="Interior Styling">Interior Styling</option>
                  </select>
                </div>
              </div>

              {/* Date & Time */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="booking-date" className="block text-sm font-medium text-gray-800 mb-1.5">
                    Preferred Date <span className="text-teal-600">*</span>
                  </label>
                  <input
                    type="date"
                    id="booking-date"
                    name="date"
                    required
                    min={today}
                    value={formData.date}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm outline-none cursor-pointer"
                  />
                </div>
                <div>
                  <label htmlFor="booking-time" className="block text-sm font-medium text-gray-800 mb-1.5">
                    Preferred Time <span className="text-teal-600">*</span>
                  </label>
                  <select
                    id="booking-time"
                    name="time"
                    required
                    value={formData.time}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm outline-none cursor-pointer"
                  >
                    <option value="">Select a time</option>
                    {timeSlots.map((slot) => (
                      <option key={slot} value={slot}>{slot}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Message */}
              <div>
                <label htmlFor="booking-message" className="block text-sm font-medium text-gray-800 mb-1.5">
                  Tell Us About Your Project
                </label>
                <textarea
                  id="booking-message"
                  name="message"
                  rows={3}
                  maxLength={500}
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Briefly describe your space, style preferences, or any specific goals..."
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm outline-none resize-none"
                ></textarea>
                <p className="text-xs text-gray-400 mt-1 text-right">{formData.message.length}/500</p>
              </div>

              {submitStatus === 'error' && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-700 text-sm">Something went wrong. Please try again.</p>
                </div>
              )}

              {/* Info strip */}
              <div className="flex items-center space-x-6 py-3 px-4 bg-teal-50 rounded-lg">
                <div className="flex items-center space-x-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-time-line text-teal-600 text-sm"></i>
                  </div>
                  <span className="text-xs text-teal-700 font-medium">30 min session</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-video-line text-teal-600 text-sm"></i>
                  </div>
                  <span className="text-xs text-teal-700 font-medium">In-person or virtual</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-price-tag-3-line text-teal-600 text-sm"></i>
                  </div>
                  <span className="text-xs text-teal-700 font-medium">
                    <span className="line-through text-gray-400 mr-1">₦1,000,000</span>
                    ₦600,000
                  </span>
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting || formData.message.length > 500}
                className="w-full py-3.5 bg-teal-600 text-white rounded-full font-medium hover:bg-teal-700 transition-all hover:scale-[1.01] disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer text-sm"
              >
                {isSubmitting ? 'Booking...' : 'Confirm Consultation'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
